import {AxiosRequestConfig} from 'axios';
import {Community} from '../../models/community';
import Logger from '../../logger';
import {getAuthToken} from '../../utils';
import {APIServiceImpl, ServiceResponse} from '../api';
import {CommunityService, DiscoverCommunitiesParams, DiscoverCommunitiesResponse, ListCommunitiesParams} from './community.service';

interface CommunityMembershipResponse {
  community?: {
    _id?: string;
    badgeFrameType?: string;
    badgeResource?: {url?: string};
    description?: string;
    id?: string;
    memberCount?: number;
    name?: string;
  };
  communityId?: string;
}

interface CommunitiesPaginatedResponse {
  data?: CommunityMembershipResponse[];
  limit?: number;
  offset?: number;
  total?: number;
}

interface DiscoverCommunityResponse {
  _id?: string;
  badgeFrameType?: string;
  badgeResource?: {url?: string};
  description?: string;
  id?: string;
  memberCount?: number;
  name?: string;
}

interface DiscoverCommunitiesApiResponse {
  data?: DiscoverCommunityResponse[];
  limit?: number;
  page?: number;
  total?: number;
}

export class CommunityServiceImpl extends APIServiceImpl implements CommunityService {
  static readonly DISCOVER_COMMUNITIES = '/communities';
  static readonly LIST_ACCOUNT_COMMUNITIES = '/accounts/:accountId/communities';
  static readonly SUGGEST_COMMUNITIES = '/communities/suggestions';

  constructor() {
    super();
    this.service.interceptors.request.use((request): AxiosRequestConfig => {
      const token = getAuthToken();
      token && (request.headers.Authorization = `Bearer ${token}`);
      return request;
    });
  }

  async listCommunities(accountId: string, params?: ListCommunitiesParams): Promise<ServiceResponse<Community[]>> {
    try {
      const path = CommunityServiceImpl.LIST_ACCOUNT_COMMUNITIES.replace(':accountId', accountId);
      const queryParams = new URLSearchParams();
      if (params?.limit != null) queryParams.set('limit', String(params.limit));
      if (params?.offset != null) queryParams.set('offset', String(params.offset));
      if (params?.role) queryParams.set('role', params.role);
      if (params?.status) queryParams.set('status', params.status);
      const queryString = queryParams.toString();
      const url = queryString ? `${path}?${queryString}` : path;
      const {data: res} = await this.get(url);
      const paginatedData = res as CommunitiesPaginatedResponse;
      const list: CommunityMembershipResponse[] = Array.isArray(paginatedData?.data)
        ? paginatedData.data
        : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
        ? res
        : [];
      const communities = list
        .map((item: CommunityMembershipResponse) => item.community ?? {id: item.communityId})
        .filter((c): c is NonNullable<CommunityMembershipResponse['community']> => c != null)
        .map(c => new Community(c));
      return new ServiceResponse<Community[]>(communities);
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string}; status?: number}; message?: string};
      const error = err.response?.data?.message ?? err.message ?? err.response?.status;
      Logger.error(`GET ${CommunityServiceImpl.LIST_ACCOUNT_COMMUNITIES}: ${String(e)}: ${error}`);
      return new ServiceResponse<Community[]>(undefined, String(error));
    }
  }

  async discoverCommunities(params?: DiscoverCommunitiesParams): Promise<ServiceResponse<DiscoverCommunitiesResponse>> {
    return this.listFromDiscoverEndpoint(CommunityServiceImpl.DISCOVER_COMMUNITIES, params);
  }

  async suggestCommunities(params?: DiscoverCommunitiesParams): Promise<ServiceResponse<DiscoverCommunitiesResponse>> {
    return this.listFromDiscoverEndpoint(CommunityServiceImpl.SUGGEST_COMMUNITIES, params);
  }

  private async listFromDiscoverEndpoint(
    endpoint: string,
    params?: DiscoverCommunitiesParams,
  ): Promise<ServiceResponse<DiscoverCommunitiesResponse>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page != null) queryParams.set('page', String(params.page));
      if (params?.limit != null) queryParams.set('limit', String(params.limit));
      if (params?.category) queryParams.set('category', params.category);
      if (params?.search) queryParams.set('search', params.search);
      if (params?.includeInactive) queryParams.set('includeInactive', 'true');

      const queryString = queryParams.toString();
      const url = queryString ? `${endpoint}?${queryString}` : endpoint;
      const {data: res} = await this.get(url);
      const paginatedData = res as DiscoverCommunitiesApiResponse;
      const list: DiscoverCommunityResponse[] = Array.isArray(paginatedData?.data)
        ? paginatedData.data
        : Array.isArray(res?.data)
        ? (res.data as DiscoverCommunityResponse[])
        : [];
      const communities = list.map(c => new Community(c));

      return new ServiceResponse<DiscoverCommunitiesResponse>({
        data: communities,
        limit: paginatedData?.limit ?? params?.limit ?? communities.length,
        page: paginatedData?.page ?? params?.page ?? 1,
        total: paginatedData?.total ?? communities.length,
      });
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string}; status?: number}; message?: string};
      const error = err.response?.data?.message ?? err.message ?? err.response?.status;
      Logger.error(`GET ${endpoint}: ${String(e)}: ${error}`);
      return new ServiceResponse<DiscoverCommunitiesResponse>(undefined, String(error));
    }
  }
}
